import CheckCircle from "@mui/icons-material/CheckCircle";
import Download from "@mui/icons-material/Download";
import Error from "@mui/icons-material/Error";
import Button, { type ButtonProps } from "@mui/material/Button";
import { type SvgIconProps } from "@mui/material/SvgIcon";
import CircularProgress from "@mui/material/CircularProgress";
import React, { useState } from "react";

import type { JSX } from "react";

type ButtonState = "idle" | "loading" | "success" | "error";

export type DownloadButtonProps = {
	/**
	 * Promise that performs the download operation
	 */
	onDownload: () => Promise<void>;

	/**
	 * Optional button label
	 */
	label?: string;

	/**
	 * Additional Material-UI Button props
	 */
	buttonProps?: Omit<ButtonProps, "onClick" | "disabled" | "startIcon">;

	/**
	 * Additional props for the icon components
	 */
	iconProps?: Omit<SvgIconProps, "fontSize" | "color">;

	/**
	 * Icon to display when idle
	 */
	defaultIcon?: JSX.Element;
	title?: string;
};

const NOTIFICATION_DURATION_MS = 3000;

/**
 * Download button with loading, success, and error states.
 * - Shows loading spinner while the download promise is running
 * - Shows green checkmark for 3 seconds on success
 * - Shows red error icon for 3 seconds on error
 * - Button is disabled during download and notification display
 */
export function DownloadButton({
	onDownload,
	label,
	buttonProps,
	iconProps,
	defaultIcon,
	title,
}: DownloadButtonProps): JSX.Element {
	const [state, setState] = useState<ButtonState>("idle");

	const handleClick = async (): Promise<void> => {
		if (state !== "idle") {
			return;
		}

		setState("loading");

		try {
			await onDownload();
			setState("success");

			// Reset to idle after notification duration
			setTimeout(() => {
				setState("idle");
			}, NOTIFICATION_DURATION_MS);
		} catch (error) {
			setState("error");

			// Reset to idle after notification duration
			setTimeout(() => {
				setState("idle");
			}, NOTIFICATION_DURATION_MS);
		}
	};

	const getIcon = (): JSX.Element => {
		switch (state) {
			case "loading":
				return <CircularProgress size={20} color="primary" />;
			case "success":
				return <CheckCircle sx={{ color: "success.main" }} />;
			case "error":
				return <Error sx={{ color: "error.main" }} />;
			case "idle":
			default:
				return defaultIcon ?? <Download {...iconProps} />;
		}
	};

	const isDisabled = state !== "idle";
	const icon = getIcon();

	// If no label is provided, render icon as children (centered)
	// If label is provided, use startIcon (left-aligned with text)
	return label != null ? (
		<Button
			title={title}
			startIcon={icon}
			onClick={handleClick}
			disabled={isDisabled}
			{...buttonProps}>
			{label}
		</Button>
	) : (
		<Button title={title} onClick={handleClick} disabled={isDisabled} {...buttonProps}>
			{icon}
		</Button>
	);
}
